/**
 * Scenarios for Site B — Lower Sable (docs/SCENARIOS.md §8–9).
 *
 * Run-of-river discipline: you are not storing anything, you are *passing* things in the right
 * proportions. Both scenarios are about what happens when the proportions are dictated to you.
 */

import type { ScenarioConfig } from '../engine/types';

const SITE = 'lower-sable';

export const fishWindow: ScenarioConfig = {
  id: 's8-fish-window',
  index: 8,
  name: 'Fish Window',
  siteId: SITE,
  start: '2026-04-25T00:00',
  durationH: 240,
  teaches: 'Spill fractions, TDG, the lock pool, and 14-gate sequencing.',
  briefing:
    'The juvenile passage program is running. Thirty per cent of instantaneous outflow spilled ' +
    'between 18:00 and 06:00, twenty per cent during the day, until 20 June. The TDG cap is waived ' +
    'to 120 % for the duration.\n\n' +
    'The two requirements pull against each other: spilling more moves more fish and makes more gas. ' +
    'Fourteen gates give you the resolution to spread the flow thin, which is the whole trick — ' +
    'gas production goes with discharge per metre of gate width, not with total discharge.\n\n' +
    'The navigation lock needs 116.8 m from 06:00 to 22:00, and you have 1.5 m of pondage in total.',
  initial: {
    headwater: 117.6,
    swe: [40, 320, 640],
    baseflow: 2400,
    wetness: 0.5,
    unitsOnline: [0, 1, 2, 3, 4, 5],
    initialSetpointMW: 80,
  },
  // Calibrated: a steady 2,400–4,500 m³/s. This scenario is about spill fractions and gas, not
  // about flood — the river must stay comfortably inside plant capacity for the trade to bite.
  weather: [
    { h: 0, tempC: 6, precipMmH: 0.03 },
    { h: 96, tempC: 7.5, precipMmH: 0.03 },
    { h: 168, tempC: 7, precipMmH: 0.04 },
    { h: 240, tempC: 8.5, precipMmH: 0.03 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E15', atH: 0, durationH: 240, params: { nightFraction: 0.3, dayFraction: 0.2 } },
    { id: 'E09', atH: 96, durationH: 30, params: { rate: 0.08 } },
  ],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'tdg', text: 'Hold TDG under the 120 % waiver', kind: 'env-compliance-at-least', value: 95 },
    { id: 'band', text: 'Hold the pool near 117.6 m', kind: 'hw-in-band-at-end', value: 117.6 },
  ],
  debriefHints: [
    'TDG depends on discharge per metre of gate width, not on total spill. Fourteen gates at 3 m each produce far less gas than four gates at 10 m for the same flow — spreading the spill is the single most effective thing you can do.',
  ],
  gradeThresholds: [7_000_000, 5_500_000, 4_000_000, 2_000_000],
};

export const bigWater: ScenarioConfig = {
  id: 's9-big-water',
  index: 9,
  name: 'Big Water',
  siteId: SITE,
  start: '2026-06-05T00:00',
  durationH: 144,
  teaches: 'A 12,000 m³/s mainstem flood with no storage to absorb it.',
  briefing:
    'The mainstem is coming up hard. By the third day you will be passing something close to ' +
    '12,000 m³/s past a plant whose hydraulic capacity is 4,850.\n\n' +
    'Seven thousand cubic metres per second goes over fourteen gates. The tailwater rises about ' +
    '4 m across that range, so your net head collapses from 19 m toward 14 and the machines lose a ' +
    'quarter of their output just as you need them most. TDG is unmanageable without the waiver.\n\n' +
    'This is survival, not optimisation. Keep the pool where it belongs, keep the gates balanced, ' +
    'and do not lose a machine you will not get back.',
  initial: {
    headwater: 117.5,
    swe: [0, 180, 520],
    baseflow: 4200,
    wetness: 0.6,
    unitsOnline: [0, 1, 2, 3, 4, 5, 6, 7],
    initialSetpointMW: 85,
    gateOpenings: [0, 0, 0, 0, 0, 1.2, 1.6, 1.6, 1.2, 0, 0, 0, 0, 0],
  },
  // Calibrated to peak near the 12,000 m³/s the briefing promises: 2.5× plant hydraulic capacity.
  weather: [
    { h: 0, tempC: 8, precipMmH: 0.04 },
    { h: 36, tempC: 12, precipMmH: 0.16 },
    { h: 72, tempC: 14, precipMmH: 0.24 },
    { h: 108, tempC: 11, precipMmH: 0.08 },
    { h: 144, tempC: 8, precipMmH: 0.04 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E02', atH: 12, durationH: 96 },
    { id: 'E23', atH: 80, durationH: 8 },
    { id: 'E26', atH: 24, durationH: 84 },
  ],
  hazardRate: 1.2,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'mfl', text: 'Keep the pool below the maximum flood level', kind: 'hw-below', value: 119.5 },
    { id: 'safety', text: 'Safety grade B or better', kind: 'safety-at-least', value: 1 },
  ],
  debriefHints: [
    'Net head collapses as the tailwater rises. At 12,000 m³/s total river flow the tailwater is about 4.5 m above its low-flow level, which is a quarter of your gross head gone. Generation falls even at full gate.',
  ],
  gradeThresholds: [4_000_000, 2_500_000, 1_000_000, -2_000_000],
};

export const lowerSableScenarios = [fishWindow, bigWater];
